import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { take } from 'rxjs/operators';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './barcode-modal.component.html',
    styleUrls: ['./barcode-modal.component.scss'],
    standalone: false
})
export class BarcodeModalComponent implements AfterViewInit, OnDestroy {
    @ViewChild('scannerVideo', { static: true }) scannerVideo: ElementRef<HTMLVideoElement>;
    code: string = null;
    scanning = true;
    error: string = null;

    private reader: BrowserMultiFormatReader | null = null;
    private controls: { stop: () => void } | null = null;

    constructor(public dialogRef: MatDialogRef<BarcodeModalComponent>, private ref: ChangeDetectorRef) {}

    ngOnDestroy() {
        this.ref.detach();
        this.stopScanner();
    }

    ngAfterViewInit() {
        this.dialogRef.afterOpened().pipe(take(1)).subscribe(() => this.startScanner());
    }

    private async startScanner() {
        const hints = new Map<DecodeHintType, any>();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.ITF,
            BarcodeFormat.EAN_13,
            BarcodeFormat.UPC_A,
            BarcodeFormat.QR_CODE,
            BarcodeFormat.DATA_MATRIX,
        ]);

        this.reader = new BrowserMultiFormatReader(hints);

        try {
            const devices = await BrowserMultiFormatReader.listVideoInputDevices();
            const device = devices.find(d => /back|rear|environment/i.test(d.label)) ?? devices[0];

            this.controls = await this.reader.decodeFromVideoDevice(
                device?.deviceId,
                this.scannerVideo.nativeElement,
                (result, err) => {
                    if (result) {
                        this.scanning = false;
                        this.code = result.getText();
                        this.stopScanner();
                        if (!this.ref['destroyed']) this.ref.detectChanges();
                    }
                    if (err && (err.name === 'NotAllowedError' || err.name === 'NotFoundError')) {
                        this.error = `Camera error: ${err.message}`;
                        if (!this.ref['destroyed']) this.ref.detectChanges();
                    }
                }
            );
        } catch (err: any) {
            this.error = `Scanner failed to start: ${err?.message ?? err}`;
            if (!this.ref['destroyed']) this.ref.detectChanges();
        }
    }

    private stopScanner() {
        try { this.controls?.stop(); } catch (_) {}
        this.controls = null;
        this.reader = null;
    }

    cancelScanning() {
        this.stopScanner();
        this.dialogRef.close(null);
    }

    close() {
        this.stopScanner();
        this.dialogRef.close(this.code);
    }
}
